import cloneDeep from 'lodash/cloneDeep';
import pickBy from 'lodash/pickBy';

import type { LogicCondition, LogicVariant } from '@webapp/common/entities/logic';
import { initialLogicCondition, initialLogicVariant } from '@webapp/common/entities/logic';
import { updateItemInArray } from '@webapp/common/lib/utils';
import { CANTRATE_ID, CANTRATE_TEXT } from '@webapp/common/resources/constants';
import { LogicBoolFunc, QuestionType } from '@webapp/common/resources/survey';

import type { Page } from '../common';

import type { LogicPageQuestion } from './question';

export const createNpsVariants = (variants: Array<LogicVariant> = []): Array<LogicVariant> =>
    Array(11)
        .fill(0)
        .map((_, i) => ({
            ...cloneDeep(initialLogicVariant),
            id: i,
            name: String(i),
            checked: variants.some(({ id }) => id === i),
        }));

export const createNpsCondition = (
    id: number,
    params: Partial<LogicCondition>,
    qParams?: { cantRate?: boolean; cantRateText?: string },
): LogicCondition => ({
    ...cloneDeep(initialLogicCondition),
    ...params,
    variants: (() => {
        const variants = createNpsVariants();
        if (qParams?.cantRate) {
            variants.push(createCantRateVariant(false, qParams.cantRateText));
        }
        return variants;
    })(),
    id,
    expand: true,
});

export const createStarVariants = (count: number, variants: Array<LogicVariant> = []): Array<LogicVariant> =>
    Array(count)
        .fill(0)
        .map((_, i) => ({
            ...cloneDeep(initialLogicVariant),
            id: i + 1,
            name: String(i + 1),
            checked: !!variants.find(({ id }) => id === i + 1),
        }));

export const createStarCondition = (
    id: number,
    count: number,
    params: Partial<LogicCondition>,
    qParams?: { cantRate?: boolean; cantRateText?: string },
): LogicCondition => ({
    ...cloneDeep(initialLogicCondition),
    ...params,
    variants: (() => {
        const variants = createStarVariants(count);
        if (qParams?.cantRate) {
            variants.push(createCantRateVariant(false, qParams.cantRateText));
        }
        return variants;
    })(),
    id,
    expand: true,
});

export const mapVariantCol = ({ ID, NAME, ORDER }: { ID: number; NAME: string; ORDER: number }): LogicVariant => ({
    ...cloneDeep(initialLogicVariant),
    id: ID,
    name: NAME,
    checked: false,
    order: ORDER,
});

export const variantsWithBoolFunc: Set<QuestionType> = new Set([
    QuestionType.TEST_FEW_OF_LIST,
    QuestionType.SELECT_FEW_IMAGE,
    QuestionType.FEW_OF_LIST,
    QuestionType.MATRIX_FEW_ANSWERS,
]);

export const variantsMultiSelect: Set<QuestionType> = new Set([
    QuestionType.TEST_FEW_OF_LIST,
    QuestionType.SELECT_FEW_IMAGE,
    QuestionType.FEW_OF_LIST,
    QuestionType.MATRIX_FEW_ANSWERS,
]);

export const isMultiSelectQuestion = (type: QuestionType, dropdownType?: 'multi' | 'single'): boolean =>
    variantsMultiSelect.has(type) || (type === QuestionType.DROPDOWN_LIST && dropdownType === 'multi');

export const variantsWithMatrix: Set<QuestionType> = new Set([
    QuestionType.MATRIX_SINGLE_ANSWER,
    QuestionType.MATRIX_FEW_ANSWERS,
    QuestionType.MATRIX_RATING,
]);

// матрицы, у которых в логике нет сетки вариантов, но «Не могу оценить» показывается отдельным столбцом построчно
export const variantsWithCantRateColumn: Set<QuestionType> = new Set([
    QuestionType.MATRIX_DROPDOWN_LIST,
    QuestionType.MATRIX_TEXT_ANSWER,
]);

export const variantsWithValues: Set<QuestionType> = new Set([
    QuestionType.TEST_FEW_OF_LIST,
    QuestionType.TEST_ONE_OF_LIST,
    QuestionType.NPS,
    QuestionType.SMILE,
    QuestionType.STAR,
    QuestionType.SCALE,
    QuestionType.DROPDOWN_LIST,
    QuestionType.SELECT_FEW_IMAGE,
    QuestionType.SELECT_ONE_IMAGE,
    QuestionType.FEW_OF_LIST,
    QuestionType.ONE_OF_LIST,
    QuestionType.MATRIX_SINGLE_ANSWER,
    QuestionType.MATRIX_FEW_ANSWERS,
    QuestionType.MATRIX_RATING,
    QuestionType.FIRST_CLICK,
    QuestionType.FIGMA_TEST,
]);

export const variantsInColumns: Set<QuestionType> = new Set([QuestionType.SCALE]);

export const variantsWidthRadioGroup: Set<QuestionType> = new Set([
    QuestionType.TEST_ONE_OF_LIST,
    QuestionType.NPS,
    QuestionType.SMILE,
    QuestionType.STAR,
    QuestionType.SCALE,
    QuestionType.DROPDOWN_LIST,
    QuestionType.SELECT_ONE_IMAGE,
    QuestionType.ONE_OF_LIST,
]);

export const createCantRateVariant = (
    checked = false,
    name?: string,
    order = Number.MAX_SAFE_INTEGER,
): LogicVariant => ({
    ...cloneDeep(initialLogicVariant),
    id: CANTRATE_ID,
    name: name || CANTRATE_TEXT,
    checked,
    order,
});

export const createCondition = (
    id: number,
    { cols, rows, type, id: qId, params: qParams },
    params: Partial<LogicCondition>,
): LogicCondition => ({
    ...cloneDeep(initialLogicCondition),
    ...params,
    id,
    boolFunc:
        type === QuestionType.MATRIX_SINGLE_ANSWER || type === QuestionType.MATRIX_RATING
            ? LogicBoolFunc.AND
            : LogicBoolFunc.OR,
    expand: true,
    variants: (() => {
        const data = variantsInColumns.has(type) ? cols : rows;

        if (variantsWithMatrix.has(type)) {
            const variants: Array<LogicVariant> = [];
            let modCols: Array<{ id: number }> = cols;
            if (type === QuestionType.MATRIX_RATING) {
                modCols = new Array(qParams.amount).fill(null).map((_, idx) => ({ id: idx + 1 }));
            }
            if (qParams.cantRate) {
                modCols = [...modCols, { id: CANTRATE_ID }];
            }
            modCols.forEach((col, colIdx) => {
                rows.forEach((row: { id: number }, rowIdx: number) => {
                    variants.push({
                        ...cloneDeep(initialLogicVariant),
                        id: `${qId}-${col.id}-${row.id}`,
                        name: '',
                        checked: false,
                        order: colIdx * rows.length + rowIdx,
                    });
                });
            });
            return variants;
        }

        if (variantsWithCantRateColumn.has(type)) {
            if (!qParams.cantRate) {
                return [];
            }
            return rows.map((row: { id: number }, rowIdx: number) => ({
                ...cloneDeep(initialLogicVariant),
                id: `${qId}-${CANTRATE_ID}-${row.id}`,
                name: '',
                checked: false,
                order: rowIdx,
            }));
        }

        const variants: Array<LogicVariant> = data.map(
            ({ id, order, value }: { id: number; order: number; value: string }) => ({
                ...cloneDeep(initialLogicVariant),
                id,
                name: value,
                checked: false,
                order,
            }),
        );
        if (qParams.cantRate && variantsWithValues.has(type)) {
            variants.push(createCantRateVariant(false, qParams.cantRateText));
        }
        return variants;
    })(),
});

export const updateCondition = (
    pages: Array<Page>,
    pageId: number,
    questionId: number,
    conditionId: number,
    callback: (condition: LogicCondition) => void | LogicCondition,
): void => {
    updateItemInArray(pages, pageId, (p: Page) => {
        updateItemInArray(p.questions, questionId, (q: LogicPageQuestion) => {
            q.conditions = updateItemInArray(q.conditions, conditionId, (c: LogicCondition) => callback(c) || c);
        });
    });
};

const logicParamWhitelist = new Set(['id', 'checked']);

export const prepareVariantsParams = (variants: Array<LogicVariant>): string => {
    let params: Array<Partial<LogicVariant>> = variants.filter(({ checked }: LogicVariant) => checked === true);
    params = params.map((v) => pickBy(v, (_, k) => logicParamWhitelist.has(k)));
    return JSON.stringify(params);
};

export const getOnlyActiveScaleVariants = (question: LogicPageQuestion, variants: Array<Partial<LogicVariant>>) => {
    if (question.type !== QuestionType.SCALE) {
        return variants;
    }

    if (question.params.scaleType === 'number') {
        const alreadyIn: Record<string, boolean> = {};
        return variants
            .map((variant) => {
                if (variant.id === CANTRATE_ID) {
                    return variant;
                }
                const col: any = question.cols.find((col) => col.id === variant.id);
                if (
                    col &&
                    typeof col.numeric_value === 'number' &&
                    question.params.scaleStartDia <= col.numeric_value &&
                    question.params.scaleEndDia >= col.numeric_value &&
                    !alreadyIn[col.numeric_value]
                ) {
                    alreadyIn[col.numeric_value] = true;
                    return { ...variant, name: col.numeric_value.toString() };
                }
                return null;
            })
            .filter((s) => s !== null)
            .sort((a, b) => (a.id === CANTRATE_ID ? 1 : b.id === CANTRATE_ID ? -1 : a.name - b.name));
    }
    return variants;
};
